import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { startSignIn, submitOtp } from '@/lib/auth';
import { useAuth } from '@/lib/auth-context';
import { useToast } from '@/components/ui/toast';

type Stage = 'email' | 'otp';

export default function SignInPage() {
  const [stage, setStage] = React.useState<Stage>('email');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { push } = useToast();

  async function onEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await startSignIn(email.trim());
      setStage('otp');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      push({ title: 'Sign-in failed', description: msg, variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function onOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitOtp(code.trim());
      if (res.isSignedIn) {
        await refresh();
        navigate('/', { replace: true });
      } else {
        push({ title: 'Wrong code', description: 'Try again.', variant: 'error' });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Try again.';
      push({ title: 'Sign-in failed', description: msg, variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-6">
          <div className="text-2xl font-bold tracking-tight">Guildhall</div>
          <div className="text-xs text-neutral-500 mt-1">AI Guild operational home</div>
        </div>
        {stage === 'email' && (
          <form onSubmit={onEmailSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@trackforce.com" />
            </div>
            <Button type="submit" disabled={busy}>{busy ? 'Sending code…' : 'Send sign-in code'}</Button>
            <div className="text-2xs text-neutral-500 text-center">A 6-digit code will be emailed to you.</div>
          </form>
        )}
        {stage === 'otp' && (
          <form onSubmit={onOtpSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="code">Code</Label>
              <Input id="code" inputMode="numeric" pattern="\d*" maxLength={6} required autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" />
              <div className="text-2xs text-neutral-500">Sent to {email}</div>
            </div>
            <Button type="submit" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Sign in'}</Button>
            <button type="button" className="text-2xs text-neutral-500 hover:text-neutral-800" onClick={() => { setStage('email'); setCode(''); }}>← Use a different email</button>
          </form>
        )}
      </Card>
    </div>
  );
}
